// frontend_react/src/pages/restaurant/FloorPlan.jsx

import React, { useEffect, useState } from "react";
import '../../styles/restaurant.css'

function FloorPlan({ setScreen, ouvrirTable, fermerTable, orders, allRooms, fusionnerTables }) {
  const [activeRoomId, setActiveRoomId] = useState(allRooms[0]?.uuid || null)
  const [displayFilter, setDisplayFilter] = useState("total")
  const room = allRooms.find(r => r.uuid === activeRoomId) || allRooms[0]

  const [couvertsModal, setCouvertsModal] = useState(null)
  const [quantityCouverts, setQuantityCouverts] = useState(0)
  const [fusionMode, setFusionMode] = useState(false)
  const [fusionTableList, setFusionTableList] = useState([])

  const getTableStatus = (tableNumber) => {
    const order = orders?.[tableNumber]
    if (!order) return 'free'
    if (order.preparedCart?.length > 0 && order.cart?.length === 0) return 'ready'
    if (order.cart?.length > 0) return 'ordering'
    return 'served'
  }

  const getTableTotal = (tableNumber) => {
    const order = orders?.[tableNumber]
    if (!order) return null
    const total = [...(order.cart || []), ...(order.preparedCart || [])]
      .reduce((sum, i) => sum + (i.price * i.quantite), 0) / 100
    return total > 0 ? total.toFixed(2) : null
  }

  const getMasterTable = (tableNumber) => {
    for (const [masterNum, order] of Object.entries(orders)) {
      if (order.linkedWith && order.linkedWith.includes(tableNumber)) return parseInt(masterNum)
    }
    return null
  }

  const handleTableClick = (table) => {
    if (fusionMode) {
      if (getMasterTable(table.number)) return
      if (orders?.[table.number]?.linkedWith?.length > 0) return
      if (fusionTableList.includes(table.number)) {
        setFusionTableList(prev => prev.filter(t => t !== table.number))
      } else {
        setFusionTableList(prev => [...prev, table.number])
      }
      return
    }
    const master = getMasterTable(table.number)
    const targetTable = master || table.number
    if (orders?.[targetTable]) {
      if (!orders[targetTable].couverts) {
        setQuantityCouverts(0)
        setCouvertsModal({ number: targetTable })
      } else {
        ouvrirTable(targetTable)
      }
    } else {
      setQuantityCouverts(0)
      setCouvertsModal(table)
    }
  }

  const executeFusion = async () => {
    if (fusionTableList.length < 2) { setFusionMode(false); setFusionTableList([]); return }
    await fusionnerTables(fusionTableList)
    setFusionMode(false)
    setFusionTableList([])
  }

  const getDisplayNumber = (tableNumber) => {
    const master = getMasterTable(tableNumber)
    if (master) return `${master} (${tableNumber})`
    const order = orders?.[tableNumber]
    if (order?.linkedWith?.length > 0) return `${tableNumber} (+${order.linkedWith.join(',')})`
    return `${tableNumber}`
  }

  useEffect(() => {
    if (!allRooms?.length) {
      setActiveRoomId(null);
      return;
    }

    const stillExists = allRooms.some((room) => room.uuid === activeRoomId);

    if (!stillExists) {
      setActiveRoomId(allRooms[0].uuid);
    }
  }, [allRooms, activeRoomId]);

  if (!room) return <p>Aucune salle configurée.</p>

  return (
    <div className="floor-plan">
      {couvertsModal && (
        <div className="modal-overlay" onClick={() => setCouvertsModal(null)}>
          <div className="modal modal--small" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header"><h2 className="modal__title">Table {couvertsModal.number}</h2></div>
            <div className="modal__body">
              <label htmlFor="restaurant-couverts-input">Nombre de couverts</label>
                <input
                  id="restaurant-couverts-input"
                  className="modal__input"
                  type="number"
                  value={quantityCouverts}
                  min={0}
                  onChange={(e) => setQuantityCouverts(parseInt(e.target.value) || 0)}
                  autoFocus
                />
            </div>
            <div className="modal__footer">
              <button className="action-btn" onClick={() => setCouvertsModal(null)}>Annuler</button>
              <button className="confirm-btn" onClick={() => { ouvrirTable(couvertsModal.number, quantityCouverts); setCouvertsModal(null) }}>Ouvrir</button>
            </div>
          </div>
        </div>
      )}

      {fusionMode && (
        <div className="fusion-banner">Mode fusion — sélectionnez les tables{fusionTableList.length > 0 && ` (${fusionTableList.join(', ')})`}</div>
      )}

      <div className="room-tabs">
        {allRooms.map(r => (
          <button key={r.uuid} className={`room-tab ${activeRoomId === r.uuid ? 'room-tab--active' : ''}`}
            onClick={() => setActiveRoomId(r.uuid)}>{r.name}</button>
        ))}
      </div>

      <div className="floor-plan__panel">
        <div className="room-canvas">
          {(room.tables || []).map(table => {
            const master = getMasterTable(table.number)
            const status = master ? getTableStatus(master) : getTableStatus(table.number)
            const total = master ? getTableTotal(master) : getTableTotal(table.number)
            const isSelected = fusionTableList.includes(table.number)
            return (
              <button key={table.uuid || table.number}
                className={`table table--${status}${isSelected ? ' table--selected' : ''}${master ? ' table--linked' : ''}`}
                style={{ left: table.position_x, top: table.position_y }}
                onClick={() => handleTableClick(table)}>
                <span className="table__number">{getDisplayNumber(table.number)}</span>
                {displayFilter === "total" && (orders?.[table.number] || master) && total && <span className="table__price">{total} €</span>}
                {displayFilter === "couverts" && (orders?.[table.number] || master) && <span className="table__price">{orders?.[master || table.number]?.couverts} cvts</span>}
                {displayFilter === "time" && (orders?.[table.number] || master) && <span className="table__price">{Math.floor((Date.now() - (orders?.[master || table.number]?.createdAt || Date.now())) / 60000)} min</span>}
              </button>
            )
          })}
          {room.decos?.map(d => (
            <div key={d.uuid} style={{ position: 'absolute', left: d.position_x, top: d.position_y }}>
              <img src={`/sprites/${d.sprite}.png`} alt="" />
            </div>
          ))}
        </div>

        <div className="action-bar">
          <div className="action-group">
            <button className={`action-btn${displayFilter === 'total' ? ' action-btn--primary' : ''}`} onClick={() => setDisplayFilter("total")}>Total</button>
            <button className={`action-btn${displayFilter === 'time' ? ' action-btn--primary' : ''}`} onClick={() => setDisplayFilter("time")}>Temps</button>
            <button className={`action-btn${displayFilter === 'couverts' ? ' action-btn--primary' : ''}`} onClick={() => setDisplayFilter("couverts")}>Couverts</button>
          </div>
          <div className="action-group">
            <button className={`action-btn${fusionMode ? ' action-btn--primary' : ''}`}
              onClick={() => { if (!fusionMode) { setFusionMode(true); setFusionTableList([]) } else executeFusion() }}>
              {fusionMode ? "Ok" : "Fusionner"}
            </button>
            {fusionMode && <button className="action-btn" onClick={() => { setFusionMode(false); setFusionTableList([]) }}>Annuler fusion</button>}
            <button className="action-btn" disabled>Modifier</button>
            <button className="action-btn" disabled>Annuler</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FloorPlan